import React from 'react';
import { FieldErrors } from 'react-hook-form';
import { IconAlertTriangle } from '@tabler/icons-react';
import { CreateBookingFormV4 } from '../../../../services/create-booking-v4/schema/CreateBookingSchemaV4';
import { startCase } from 'lodash';

interface ErrorSummaryPropsV4 {
    errors: FieldErrors<CreateBookingFormV4>;
    onSelectTab: (tabKey: string) => void;
}

// Tipe untuk hasil error yang sudah di-flat-kan
interface FlatError {
    key: string;
    message: string;
    tab: string;
}

// Peta untuk menghubungkan nama field dengan tab yang benar
const fieldToTabMap: Record<string, string> = {
    'invoice_date': 'invoice',
    'status': 'invoice',
    'mark': 'invoice',
    'customer_phone': 'guest_details',
    'customer_address': 'guest_details',
    'customer_country': 'guest_details',
    'customer_state': 'guest_details',
    'customer_city': 'guest_details',
    'customer_zip': 'guest_details',
    'customer_date_of_birth': 'guest_details',
    'customer_identity_number': 'guest_details',
    'agent_id': 'guest_details',
    'public_note': 'comments',
    'private_note': 'comments',
    'payments': 'payment',
};

// Label ramah-pengguna untuk field per-kamar (default startCase menghasilkan
// "Rate Plan Id" / "Room Type Id" yang membingungkan user awam).
const fieldLabelMap: Record<string, string> = {
    rate_plan_id: 'Rate plan',
    room_type_id: 'Room type',
    room_id: 'Room number',
    customer_name: 'Guest name',
    customer_surname: 'Guest surname',
    customer_email: 'Guest email',
    checkin_date: 'Check-in date',
    checkout_date: 'Check-out date',
    payment_type: 'Payment type',
    adults: 'Adults',
    children: 'Children',
    rate: 'Rate',
    amount: 'Payment amount',
    surcharge: 'Surcharge',
    status: 'Status',
    mark: 'Mark',
};

// PERBAIKAN UTAMA: Fungsi rekursif yang baru untuk mem-flat-kan semua error
const getErrorMessages = (errors: FieldErrors, pathPrefix = '', roomIndex: number | null = null): FlatError[] => {
    let messages: FlatError[] = [];

    for (const key in errors) {
        const error = errors[key as keyof typeof errors] as any;
        const currentPath = pathPrefix ? `${pathPrefix}.${key}` : key;

        if (error) {
            // Kasus 1: Ini adalah FieldError, kita ambil pesannya
            if (typeof error.message === 'string') {
                const tab = roomIndex !== null ? 'invoice' : fieldToTabMap[key] || 'invoice';
                const label = roomIndex !== null
                    ? `Room ${roomIndex + 1} - ${fieldLabelMap[key] ?? startCase(key)}`
                    : startCase(key);

                messages.push({
                    key: currentPath,
                    message: `${label}: ${error.message}`,
                    tab: tab,
                });
            }
            // Kasus 2: Ini adalah array (seperti `bookingroom` atau `payments`)
            else if (Array.isArray(error)) {
                error.forEach((item, index) => {
                    if (item) {
                        // Lakukan rekursi untuk setiap item di dalam array
                        // Di dalam kamar (mis. payments), pertahankan nomor kamar — bukan indeks item bersarang.
                        messages = messages.concat(getErrorMessages(item, currentPath, roomIndex ?? index));
                    }
                });
            }
        }
    }
    return messages;
};

const ErrorSummaryV4: React.FC<ErrorSummaryPropsV4> = ({ errors, onSelectTab }) => {
    const errorMessages = getErrorMessages(errors);

    if (errorMessages.length === 0) {
        return null;
    }

    const handleJumpToError = (tab: string) => {
        onSelectTab(tab);
    };

    return (
        <div className="alert alert-danger mb-4" role="alert">
            <div className="d-flex">
                <IconAlertTriangle />
                <div className="ms-3">
                    <h4 className="alert-title">Please fix the following issues:</h4>
                    <div className="text-muted">
                        <ul className="list-unstyled mb-0">
                            {errorMessages.map(({ key, message, tab }) => (
                                <li key={key} className="mt-1">
                                    <a href="#" className="alert-link" onClick={(e) => { e.preventDefault(); handleJumpToError(tab); }}>
                                        {message}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ErrorSummaryV4;